import { useEffect, useMemo, useState } from "react";

import { Tooltip } from "antd";
import dayjs from "dayjs";

import { relativeDisplay, format as dateFormat, DateFormat, Zone } from "../../../services/TimeService/TimeService";

const MS_IN_MINUTE = 1000 * 60;
const MS_IN_HOUR = MS_IN_MINUTE * 60;
const MS_IN_14_DAYS = MS_IN_HOUR * 24 * 14;

type RVTimeDisplayProps = {
    /** The time value to display. Accepts timestamp, Date object, or date string */
    time: number | Date | string;
    /** Optional CSS class name for custom styling */
    className?: string;
    /** Date format to use for the full timestamp display in tooltip */
    format?: DateFormat;
    /** Timezone for formatting the full timestamp */
    zone?: Zone;
    /** Tooltip placement position */
    position?: "top" | "bottom" | "left" | "right";
};

/**
 * A component that displays time in a relative format with full timestamp tooltip.
 *
 * Displays times in a user-friendly relative format (e.g., "2 hours ago", "3 days ago")
 * for recent times, and shows the full formatted timestamp in a tooltip on hover.
 * For times older than 14 days, it displays the full timestamp directly.
 *
 * The component automatically updates the relative time display:
 * - Every second for times less than 2 minutes away
 * - Every minute for times less than 1 hour away
 * - Static display for times more than 1 hour away
 *
 * @example
 * ```tsx
 * // Simple usage with timestamp
 * <RVTimeDisplay time={Date.now()} />
 *
 * // With custom format and timezone
 * <RVTimeDisplay
 *   time={createdAt}
 *   format={DateFormat.DATE_TIME_DEFAULT}
 *   zone={Zone.et}
 *   position="top"
 * />
 *
 * // With custom styling
 * <RVTimeDisplay
 *   time={updatedAt}
 *   className="custom-time-display"
 * />
 * ```
 */
export function RVTimeDisplay({
    className,
    format = DateFormat.PRETTY_AT,
    position = "bottom",
    time,
    zone,
}: RVTimeDisplayProps): JSX.Element {
    const fullDisplay = useMemo(() => Math.abs(dayjs().diff(time)) > MS_IN_14_DAYS, [time]);
    const formattedTime = useMemo(() => dateFormat(time, { format, timezone: zone }), [time, format, zone]);
    const [display, setDisplay] = useState<string>(relativeDisplay(time));

    useEffect(() => {
        if (!fullDisplay) {
            const dateDiff = Math.abs(dayjs().diff(time));

            if (dateDiff < MS_IN_HOUR) {
                // if its less than 2 min away, go in seconds, otherwise go with minutes
                const cleanup = setInterval(
                    () => setDisplay(relativeDisplay(time)),
                    dateDiff < 2 * MS_IN_MINUTE ? 1000 : MS_IN_MINUTE
                );
                return () => clearInterval(cleanup);
            }
        }
    }, [display, fullDisplay, time]);

    if (fullDisplay) {
        return (
            <span className={className} data-testid="time-display">
                {formattedTime}
            </span>
        );
    }

    return (
        <Tooltip title={formattedTime} placement={position}>
            <span className={className} data-testid="time-display">
                {display}
            </span>
        </Tooltip>
    );
}
